import React, { useState } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  ArrowRight, Play, Server, Users, UserPlus, Ticket, FileText, Cloud, Shield,
  Phone, Monitor, RefreshCw, CreditCard, GitBranch, User, Package, Layers,
  BarChart2, CheckCircle, ChevronDown, TrendingUp, Zap
} from 'lucide-react';
import {
  mspsHeroData, mspsStatsData, clientManagementData, serviceStackData,
  automationFeaturesData, pricingModelsData, growthToolsData, mspsFAQData, mspTestimonialData
} from '../../data/mspsData';

const iconMap = {
  Users, UserPlus, Ticket, FileText, Cloud, Shield, Server, Phone,
  Monitor, RefreshCw, CreditCard, GitBranch, User, Package, Layers, BarChart2
};

// Hero Section
const MSPsHero = () => (
  <section className="relative min-h-screen overflow-hidden bg-dark-ellipse">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                         radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
    </div>
    <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8bcaf9]/30 rounded-full blur-3xl animate-pulse" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
        <div className="text-left">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <Server className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">{mspsHeroData.tagline}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {mspsHeroData.title}
          </h1>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
            {mspsHeroData.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full text-lg transition-all hover:scale-105 hover:shadow-xl group">
              {mspsHeroData.cta}
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all hover:scale-105 group bg-transparent">
              <Play className="mr-2 w-5 h-5" />
              {mspsHeroData.secondaryCta}
            </Button>
          </div>
        </div>

        <div className="relative hidden lg:block">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-1 shadow-2xl border border-white/20">
            <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    <Server className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-semibold">MSP Dashboard</span>
                </div>
                <span className="text-green-400 text-sm">Live</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: 'Clientes Activos', value: '156', change: '+12' },
                  { label: 'Servicios Gestionados', value: '847', change: '+45' },
                  { label: 'MRR', value: '$89.4K', change: '+18%' },
                  { label: 'Tickets Abiertos', value: '23', change: '-8' }
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/5 rounded-xl p-4">
                    <p className="text-[#8bcaf9] text-xs mb-1">{stat.label}</p>
                    <p className="text-white font-bold text-lg">{stat.value}</p>
                    <span className={`text-xs ${stat.change.startsWith('+') || stat.change.startsWith('-8') ? 'text-green-400' : 'text-red-400'}`}>{stat.change}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {['TechCorp Inc. - 45 usuarios', 'Legal Partners - 28 usuarios', 'HealthFirst - 67 usuarios'].map((client, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <span className="text-white/80 text-sm">{client}</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl transform rotate-3 hover:rotate-0 transition-transform">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-gray-900 font-bold text-sm">Automatización</p>
                <p className="text-green-500 text-xs font-medium">85% tareas auto</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {mspsStatsData.map((stat, idx) => (
          <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105">
            <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</p>
            <p className="text-white/70 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
      </svg>
    </div>
  </section>
);

// Client Management Section
const ClientManagementSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Gestión de Clientes
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Todos tus clientes,{' '}
            <span className="text-[#1297fd]">un solo panel</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
            Centraliza la gestión de todos tus clientes MSP con segregación completa de datos, permisos granulares y automatización de tareas repetitivas.
          </p>
          <div className="space-y-6">
            {clientManagementData.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="flex gap-4 group">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#1297fd] transition-colors">{feature.title}</h4>
                    <p className="text-[#c3c3c5] text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white font-semibold">Portal Multi-Cliente</span>
              <span className="text-green-400 text-sm">156 clientes</span>
            </div>
            <div className="space-y-3">
              {[
                { name: 'TechCorp Inc.', users: 45, services: 12, status: 'Activo', mrr: '$2,450' },
                { name: 'Legal Partners LLP', users: 28, services: 8, status: 'Activo', mrr: '$1,890' },
                { name: 'HealthFirst Medical', users: 67, services: 15, status: 'Activo', mrr: '$4,200' },
                { name: 'StartupXYZ', users: 12, services: 5, status: 'Trial', mrr: '$0' }
              ].map((client, idx) => (
                <div key={idx} className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">{client.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${client.status === 'Activo' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {client.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-[#8bcaf9]">
                    <span>{client.users} usuarios</span>
                    <span>{client.services} servicios</span>
                    <span className="text-white font-medium">{client.mrr}/mes</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Service Stack Section
const ServiceStackSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Stack de Servicios
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Expande tu oferta con{' '}
          <span className="text-[#1297fd]">servicios integrados</span>
        </h2>
        <p className="text-lg text-[#c3c3c5] max-w-2xl mx-auto">
          Gestiona todos los vendors y servicios cloud desde una única plataforma con provisioning automático.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {serviceStackData.map((service) => {
          const IconComponent = iconMap[service.icon];
          return (
            <div key={service.id} className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl hover:-translate-y-2">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#1297fd] transition-colors">{service.title}</h3>
              <p className="text-[#c3c3c5] text-sm mb-4">{service.description}</p>
              <div className="flex flex-wrap gap-2">
                {service.vendors.map((vendor) => (
                  <span key={vendor} className="text-xs px-2 py-1 bg-[#e3e4e6] rounded-lg text-gray-600">{vendor}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Automation Section
const AutomationSection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 border border-white/20">
          Automatización
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Elimina tareas manuales con automatización inteligente
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {automationFeaturesData.map((feature) => {
          const IconComponent = iconMap[feature.icon];
          return (
            <div key={feature.id} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
              </div>
              <h3 className="font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-white/70 text-sm">{feature.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Pricing Models Section
const PricingModelsSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <div className="grid grid-cols-2 gap-4">
            {pricingModelsData.map((model) => {
              const IconComponent = iconMap[model.icon];
              return (
                <div key={model.id} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-lg">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    {IconComponent && <IconComponent className="w-5 h-5 text-white" />}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1 text-sm">{model.title}</h4>
                  <p className="text-[#c3c3c5] text-xs">{model.description}</p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Modelos de Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Flexibilidad total en{' '}
            <span className="text-[#1297fd]">cómo cobras</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
            Adapta tu modelo de negocio con múltiples opciones de pricing. Desde per-user hasta consumo variable, configura márgenes y descuentos por cliente.
          </p>
          <ul className="space-y-3">
            {['Márgenes personalizados por cliente', 'Descuentos por volumen automáticos', 'Promociones temporales', 'Facturación consolidada'].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#1297fd]" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Growth Tools Section
const GrowthToolsSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Herramientas de Crecimiento
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Acelera tu{' '}
          <span className="text-[#1297fd]">crecimiento</span>
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {growthToolsData.map((tool, idx) => (
          <div key={tool.id} className={`rounded-2xl p-6 ${idx === 0 ? 'bg-gradient-to-br from-[#1297fd] to-[#40aaf8] text-white' : 'bg-white border border-[#e3e4e6]'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${idx === 0 ? 'bg-white/20' : 'bg-[#8bcaf9]/20'}`}>
              <span className={`font-bold ${idx === 0 ? 'text-white' : 'text-[#1297fd]'}`}>{idx + 1}</span>
            </div>
            <h4 className={`font-bold mb-2 ${idx === 0 ? 'text-white' : 'text-gray-900'}`}>{tool.title}</h4>
            <p className={`text-sm ${idx === 0 ? 'text-white/80' : 'text-[#c3c3c5]'}`}>{tool.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Testimonial Section
const TestimonialSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 border border-[#e3e4e6] shadow-xl">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
          <span className="text-white text-2xl font-bold">"</span>
        </div>
        <blockquote className="text-xl sm:text-2xl text-gray-900 mb-8 leading-relaxed">
          {mspTestimonialData.quote}
        </blockquote>
        <div>
          <p className="font-bold text-gray-900">{mspTestimonialData.author}</p>
          <p className="text-[#1297fd]">{mspTestimonialData.role}, {mspTestimonialData.company}</p>
          <p className="text-[#c3c3c5] text-sm">{mspTestimonialData.location}</p>
        </div>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="py-24 bg-[#e3e4e6]/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">FAQ</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Preguntas frecuentes</h2>
        </div>
        <div className="space-y-4">
          {mspsFAQData.map((item, idx) => (
            <div key={idx} className={`rounded-2xl border-2 transition-all overflow-hidden ${openIndex === idx ? 'border-[#8bcaf9] bg-white shadow-lg' : 'border-[#e3e4e6] bg-white'}`}>
              <button onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)} className="w-full p-6 text-left flex items-center justify-between gap-4">
                <span className={`font-semibold text-lg ${openIndex === idx ? 'text-[#1297fd]' : 'text-gray-900'}`}>{item.question}</span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openIndex === idx ? 'rotate-180' : 'bg-[#e3e4e6]'}`} style={openIndex === idx ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}>
                  <ChevronDown className={`w-5 h-5 ${openIndex === idx ? 'text-white' : 'text-gray-600'}`} />
                </div>
              </button>
              <div className={`overflow-hidden transition-all ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}>
                <div className="px-6 pb-6"><p className="text-[#c3c3c5] leading-relaxed">{item.answer}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Escala tu MSP con automatización inteligente</h2>
      <p className="text-lg text-white/80 mb-10">Gestiona más clientes, reduce tareas manuales y aumenta tus márgenes con nuestra plataforma.</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Button size="lg" className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-10 py-6 rounded-full transition-all hover:scale-105 group">
          Comenzar ahora
          <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
        </Button>
        <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-full transition-all hover:scale-105 bg-transparent">
          Solicitar demo
        </Button>
      </div>
    </div>
  </section>
);

// Main Page Component
const MSPsPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="MSPs · Plataforma all-in-one para Managed Service Providers"
        description="Gestiona clientes, stack de servicios cloud, tickets, licencias, automatización y facturación recurrente desde una sola plataforma diseñada para MSPs modernos."
        keywords="MSP, Managed Service Provider, PSA, RMM, billing MSP, gestión clientes MSP"
        canonical="/msps"
      />
      <Navbar />
      <MSPsHero />
      <ClientManagementSection />
      <ServiceStackSection />
      <AutomationSection />
      <PricingModelsSection />
      <GrowthToolsSection />
      <TestimonialSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default MSPsPage;
